/* eslint-disable @typescript-eslint/no-unused-vars */
import { memo, useCallback, useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from '@emotion/styled';
import { useFloating } from '@floating-ui/react';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import { Portal } from '@trinity/chat-components/components/Portal/index.js';
import type { IDropDownItem, ItemProps, ItemsProps, SelectionProps, Props } from './interfaces';
import { SetActiveBrandContext, UserBrandsContext } from '@trinity/chat-components/containers/UserBrands/UserBrandsContext.js';
import { useActiveBrand } from '@trinity/chat-components/containers/UserBrands/useActiveBrandIdentifier.js';
import { useSameWidthMiddleware, useZIndexMiddleware } from '@trinity/chat-components/components/presentation/popper-modifiers.js';

const DropdownRoot = styled.div(({ theme }) => ({
  width: '100%',
  position: 'relative',
  fontFamily: theme.fonts.regular,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
}));

const DropdownInput = styled.div<{ disabled: boolean }>(({ theme, disabled }) => ({
  fontSize: '14px',
  lineHeight: '14px',
  fontFamily: theme.fonts.regular,
  color: theme.palette.black.main,
  cursor: disabled ? undefined : 'pointer',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  border: '2px solid',
  borderColor: disabled
    ? theme.palette.gray.main : theme.isTrinityThemed ? theme.palette.sentiment.primary.main
      : theme.palette.btnPrimary.bg,
  padding: '10px 10px',
  borderRadius: '4px',
  marginBottom: '10px',
  width: '225px',

  '&:hover': {
    backgroundColor: disabled
      ? 'transparent' // theme.palette.gray.light2
      : theme.isTrinityThemed ? theme.palette.sentiment.primary.light : theme.palette.btnPrimary.bg,
  },
}));

const DropdownList = styled.div({
  marginTop: '-10px',
  backgroundColor: 'white',
  borderRadius: '4px',
  maxHeight: '300px',
  overflowY: 'auto',
  zIndex: 10,
  borderLeft: '1px solid #d1d5db',
  borderRight: '1px solid #d1d5db',
  borderBottom: '1px solid #d1d5db',
  borderTop: 'none',
  boxShadow: '0 4px 11px rgba(0, 0, 0, 0.11)',
});

const DropdownItem = styled.div(({ theme }) => ({
  padding: '10px 15px',
  fontFamily: theme.fonts.regular,
  fontSize: '14px',
  color: 'black',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  '&:hover': {
    backgroundColor: theme.isTrinityThemed ? theme.palette.sentiment.primary.light : theme.palette.btnPrimary.bg,
  },
}));

const ArrowDown = styled.div(({ theme }) => ({
  paddingBottom: '5px',
  '&:after': {
    content: '\'\\25BE\'',
    display: 'block',
    color: theme.palette.gray.dark1,
  },
}));

const ArrowUp = styled.div(({ theme }) => ({
  paddingBottom: '5px',
  '&:after': {
    content: '\'\\25B4\'',
    display: 'block',
    color: theme.palette.gray.dark1,
  },
}));

const Item = memo(<T extends IDropDownItem>({ onSelect, ...props }: ItemProps<T>) => {
  const handleMouseDown = useCallback(
    (item: T) => () => {
      onSelect(item);
    },
    [onSelect],
  );

  return (
    <DropdownItem onMouseDown={handleMouseDown(props.item)}>
      {props.value}
    </DropdownItem>
  );
});

const Items = memo(<T extends IDropDownItem>(props: ItemsProps<T>) => {
  return (
    <DropdownList>
      {props.items.map((item) => (
        <Item
          item={item}
          key={item.identifier}
          onSelect={props.onSelect}
          value={props.getItemValue(item)}
        />
      ))}
    </DropdownList>
  );
});

const Selection = ({
  classes = {},
  open,
  ...props
}: SelectionProps) => {
  return (
    <DropdownInput
      disabled={props.disabled}
      onClick={props.toggleVisibility} theme={undefined}      >
      <div>{props.text}</div>
      {!props.disabled && (open ? <ArrowUp /> : <ArrowDown />)}
    </DropdownInput>
  );
};

export const UserBrandDropdown = <T extends IDropDownItem>({
  classes = {},
  ...props
}: Props) => {
  const [open, setOpen] = useState(false);
  const brands = useContext(UserBrandsContext);
  const userBrand = useActiveBrand();
  const setActiveBrand = useContext(SetActiveBrandContext);
  const isReadOnly = brands?.length < 2;
  const navigate = useNavigate();

  const toggleVisibility = useCallback(() => {
    if (props.disabled || isReadOnly) return;

    setOpen((prev) => !prev);
  }, [props.disabled, isReadOnly]);

  const handleSelection = useCallback(
    (data: T) => {
      setActiveBrand(data.identifier);
      setOpen(false);
      navigate('/');
    },
    [navigate, setActiveBrand],
  );

  const sameWidthMiddleware = useSameWidthMiddleware();
  const zIndexMiddleware = useZIndexMiddleware();
  const { refs, floatingStyles } = useFloating<HTMLDivElement>({
    placement: 'bottom-start',
    open,
    onOpenChange: setOpen,
    middleware: [sameWidthMiddleware, zIndexMiddleware],
  });

  const handleBlur = useCallback(
    (e: MouseEvent) => {
      if (!refs.domReference.current.contains(e.target as Node)) {
        setOpen(false);
      }
    },
    [refs.domReference],
  );

  return (
    <DropdownRoot>
      <div ref={refs.setReference}>
        <Selection
          disabled={props.disabled || isReadOnly}
          open={open}
          text={userBrand ? userBrand.name : 'Unkown Brand Name'}
          toggleVisibility={toggleVisibility}
        />
      </div>
      {open && !isReadOnly && (
        <Portal>
          <ClickAwayListener
            mouseEvent="onMouseDown"
            onClickAway={handleBlur}
            touchEvent="onTouchStart"
          >
            <div
              ref={refs.setFloating}
              style={floatingStyles}>
              <Items
                getItemValue={(item) => { return item.name; }}
                items={brands}
                onSelect={handleSelection}
              />
            </div>
          </ClickAwayListener>
        </Portal>
      )}
    </DropdownRoot>
  );
};

export default UserBrandDropdown;